import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoneyInput } from "@/components/common/MoneyInput";
import { FactorajeExposicion, FactorajeResumen } from "@/components/solicitudes/FactorajeResumen";
import { api } from "@/lib/api";
import { mxn } from "@/lib/format";
import { estaCancelada, estaSellada } from "@/lib/selloSolicitud";
import { calcularAnticipo, rangoVencimiento, GARANTIA_FACTORAJE, FUENTE_PAGO_FACTORAJE } from "@/lib/factoraje";

const Campo = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="mb-1 block text-xs font-medium text-slate-600">{label}</Label>
    {children}
  </div>
);

const Fijo = ({ testid, children }) => (
  <div data-testid={testid}
    className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-600">
    <Lock className="h-3.5 w-3.5 shrink-0 text-slate-400" /><span className="truncate">{children}</span>
  </div>
);

function useExposicion(deudorId, productoId, solicitudId) {
  const [exp, setExp] = useState(null);
  useEffect(() => {
    if (!deudorId) { setExp(null); return; }
    api.get("/factoraje/exposicion", { params: { deudor_id: deudorId, producto_id: productoId, solicitud_id: solicitudId || "" } })
      .then((r) => setExp(r.data)).catch(() => setExp(null));
  }, [deudorId, productoId, solicitudId]);
  return exp;
}

// Bloque «Datos de la factura» de la solicitud cuando el producto es de factoraje.
export function FactorajeCaptura({ form, onChange, producto }) {
  const [deudores, setDeudores] = useState([]);
  const f = form.factoraje && typeof form.factoraje === "object" ? form.factoraje : {};
  const bloqueada = estaSellada(form) || estaCancelada(form);
  const exp = useExposicion(bloqueada ? null : f.deudor_id, form.producto_id, form.id);
  useEffect(() => {
    if (!bloqueada) api.get("/factoraje/deudores").then((r) => setDeudores(r.data || [])).catch(() => setDeudores([]));
  }, [bloqueada]);

  if (bloqueada) return <FactorajeResumen factoraje={f} exposicion={form.factoraje_exposicion} testid="factoraje-sellado" />;

  const aforoProd = producto?.aforo_pct;
  const aforo = f.aforo_pct ?? aforoProd ?? "";
  const anticipo = calcularAnticipo(f.valor_nominal, aforo);
  const set = (patch) => {
    const next = { ...f, aforo_pct: aforo, base_dias: producto?.base_dias_descuento ?? 360, ...patch };
    next.anticipo = calcularAnticipo(next.valor_nominal, next.aforo_pct);
    onChange("factoraje", next);
  };
  const venc = rangoVencimiento(producto?.plazo_max_dias_factura);
  const aforoExcede = aforoProd != null && Number(aforo) > Number(aforoProd);
  const total = exp ? Number(exp.exposicion_actual || 0) + Number(anticipo || 0) : null;
  return (
    <div data-testid="factoraje-captura" className="grid grid-cols-1 gap-3 rounded-lg border border-indigo-200 bg-indigo-50/30 p-3 sm:grid-cols-2">
      <Campo label="Deudor de la factura *" full>
        <Select value={f.deudor_id || ""} onValueChange={(v) => {
          const d = deudores.find((x) => x.id === v);
          set({ deudor_id: v, deudor_nombre: d?.nombre || "", deudor_rfc: d?.rfc || "" });
        }}>
          <SelectTrigger data-testid="factoraje-deudor"><SelectValue placeholder="Elige un deudor de factoraje..." /></SelectTrigger>
          <SelectContent>
            {deudores.length === 0 && <div className="px-2 py-1.5 text-xs text-amber-600" data-testid="factoraje-sin-deudores">No hay clientes marcados como deudor de factoraje.</div>}
            {deudores.map((d) => (
              <SelectItem key={d.id} value={d.id} disabled={d.bloqueado} data-testid={`factoraje-deudor-opt-${d.id}`}>
                {d.nombre} · {d.rfc}{d.bloqueado ? " (bloqueado)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Campo>
      <Campo label="UUID del CFDI *" full>
        <Input data-testid="factoraje-uuid" value={f.cfdi_uuid || ""} placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
          onChange={(e) => set({ cfdi_uuid: e.target.value.toUpperCase() })} className="font-mono" />
      </Campo>
      <Campo label="Valor nominal de la factura *">
        <MoneyInput data-testid="factoraje-valor-nominal" value={f.valor_nominal ?? ""} onChange={(v) => set({ valor_nominal: v })} />
      </Campo>
      <Campo label="Fecha de vencimiento de la factura *">
        <Input data-testid="factoraje-vencimiento" type="date" min={venc.min} max={venc.max}
          value={f.fecha_vencimiento_factura || ""} onChange={(e) => set({ fecha_vencimiento_factura: e.target.value })} />
      </Campo>
      <Campo label={`Aforo (%) — máximo del producto ${aforoProd ?? "—"}%`}>
        <Input data-testid="factoraje-aforo" type="number" step="0.01" max={aforoProd} value={aforo}
          onChange={(e) => set({ aforo_pct: e.target.value })} className={aforoExcede ? "border-red-400" : ""} />
        {aforoExcede && <p className="mt-1 text-[11px] text-red-600">No puede ser mayor al aforo del producto.</p>}
      </Campo>
      <Campo label="Anticipo (valor nominal × aforo)">
        <Fijo testid="factoraje-anticipo">{anticipo != null ? mxn(anticipo) : "—"}</Fijo>
      </Campo>
      <Campo label="Base de días del descuento"><Fijo testid="factoraje-base-dias">{producto?.base_dias_descuento ?? 360} días</Fijo></Campo>
      <Campo label="Fuente de pago"><Fijo testid="factoraje-fuente-pago">{FUENTE_PAGO_FACTORAJE}</Fijo></Campo>
      <Campo label="Garantía" full>
        <Fijo testid="factoraje-garantia">
          {GARANTIA_FACTORAJE} — Factura UUID {f.cfdi_uuid || "…"} a cargo de {f.deudor_nombre || "…"}
        </Fijo>
      </Campo>
      {exp && (
        <div className="sm:col-span-2">
          <FactorajeExposicion exposicion={{ total, limite: exp.limite, otras: exp.exposicion_actual, actual: anticipo || 0,
            excede: exp.limite != null && total > Number(exp.limite) }} testid="factoraje-exposicion" />
        </div>
      )}
      <p className="sm:col-span-2 text-[11px] text-slate-500">
        El monto solicitado es el anticipo y lo calcula el sistema al guardar. Plazo, periodicidad y ancla no aplican: es una sola cuota al vencimiento de la factura.
      </p>
    </div>
  );
}
